/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Journal Ledger Hooks - React Query for SuperAdmin Journal Audit
 * ============================================================================
 */

import { useQuery } from '@tanstack/react-query';
import {
  journalLedgerService,
  type JournalEntryDto,
  type TrialBalanceDto,
  type DiscrepancyDto,
  type LedgerAccountDto,
  type LedgerHealthDto,
} from '@/services/superadmin/journal-ledger.service';
import type { ApiError, PagedResponse } from '@/services/api/types';

export const journalLedgerKeys = {
  all: ['journal-ledger'] as const,
  entries: (orgId: string, page: number, size: number, sourceType?: string) =>
    [...journalLedgerKeys.all, 'entries', { orgId, page, size, sourceType }] as const,
  entry: (id: string, orgId: string) =>
    [...journalLedgerKeys.all, 'entry', id, orgId] as const,
  correlated: (correlationId: string, orgId: string) =>
    [...journalLedgerKeys.all, 'correlated', correlationId, orgId] as const,
  trialBalance: (orgId: string, asOf?: string) =>
    [...journalLedgerKeys.all, 'trial-balance', { orgId, asOf }] as const,
  verify: (orgId: string) =>
    [...journalLedgerKeys.all, 'verify', orgId] as const,
  balanced: (orgId: string) =>
    [...journalLedgerKeys.all, 'balanced', orgId] as const,
  accounts: (orgId: string) =>
    [...journalLedgerKeys.all, 'accounts', orgId] as const,
  health: () => [...journalLedgerKeys.all, 'health'] as const,
};

export function useJournalEntries(
  orgId: string,
  page = 0,
  size = 20,
  sourceType?: string
) {
  return useQuery<PagedResponse<JournalEntryDto>, ApiError>({
    queryKey: journalLedgerKeys.entries(orgId, page, size, sourceType),
    queryFn: () => journalLedgerService.getEntries(orgId, page, size, sourceType),
    enabled: !!orgId,
    staleTime: 30 * 1000,
  });
}

export function useJournalEntry(id: string, orgId: string) {
  return useQuery<JournalEntryDto, ApiError>({
    queryKey: journalLedgerKeys.entry(id, orgId),
    queryFn: () => journalLedgerService.getEntry(id, orgId),
    enabled: !!id && !!orgId,
  });
}

export function useCorrelatedEntries(correlationId: string, orgId: string) {
  return useQuery<JournalEntryDto[], ApiError>({
    queryKey: journalLedgerKeys.correlated(correlationId, orgId),
    queryFn: () => journalLedgerService.getCorrelatedEntries(correlationId, orgId),
    enabled: !!correlationId && !!orgId,
  });
}

export function useTrialBalance(orgId: string, asOf?: string) {
  return useQuery<TrialBalanceDto, ApiError>({
    queryKey: journalLedgerKeys.trialBalance(orgId, asOf),
    queryFn: () => journalLedgerService.getTrialBalance(orgId, asOf),
    enabled: !!orgId,
    staleTime: 60 * 1000,
  });
}

export function useVerifyBalances(orgId: string) {
  return useQuery<DiscrepancyDto[], ApiError>({
    queryKey: journalLedgerKeys.verify(orgId),
    queryFn: () => journalLedgerService.verifyBalances(orgId),
    enabled: !!orgId,
    staleTime: 60 * 1000,
  });
}

export function useLedgerAccounts(orgId: string) {
  return useQuery<LedgerAccountDto[], ApiError>({
    queryKey: journalLedgerKeys.accounts(orgId),
    queryFn: () => journalLedgerService.getAccounts(orgId),
    enabled: !!orgId,
    staleTime: 60 * 1000,
  });
}

export function useLedgerHealth() {
  return useQuery<LedgerHealthDto, ApiError>({
    queryKey: journalLedgerKeys.health(),
    queryFn: journalLedgerService.getLedgerHealth,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}
